import { buildProps, definePropType, iconPropType } from '@jdesign-vue/utils'
import { UPDATE_MODEL_EVENT } from '@jdesign-vue/constants'
import { useSizeProp } from '@jdesign-vue/hooks'
import type InputTag from './input-tag.vue'
import type { TagProps } from '@jdesign-vue/components/tag'
import type { ExtractPropTypes } from 'vue'

export type InputTagSplitter = string | RegExp
export type InputTagType = TagProps['type']
export type InputTagTypeResolver = (
  tag: string,
  index: number,
  tags: string[]
) => InputTagType | undefined

export const inputTagProps = buildProps({
  /**
   * @description binding value（标签数组）
   */
  modelValue: {
    type: definePropType<string[]>(Array),
    default: undefined,
  },
  /**
   * @description input box size
   */
  size: useSizeProp,
  /**
   * @description whether to disable
   */
  disabled: Boolean,
  /**
   * @description ignore disabled from Form
   */
  ignoreFormDisabled: Boolean,
  /**
   * @description validation status
   */
  status: {
    type: String,
    values: ['success', 'warning', 'error', ''],
    default: '',
  },
  /**
   * @description whether the input has border
   */
  bordered: {
    type: Boolean,
    default: true,
  },
  /**
   * @description rounded input
   */
  round: Boolean,
  /**
   * @description placeholder
   */
  placeholder: {
    type: String,
  },
  /**
   * @description width of the input
   */
  width: {
    type: [String, Number],
  },
  /**
   * @description splitter used to split input into tags
   */
  splitter: {
    type: definePropType<InputTagSplitter>([String, RegExp]),
    default: undefined,
  },
  /**
   * @description max number of tags
   */
  maxTags: {
    type: Number,
    default: undefined,
  },
  /**
   * @description allow exceed maxTags (show length only)
   */
  enabledExceed: Boolean,
  /**
   * @description dedupe tags
   */
  unique: {
    type: Boolean,
    default: true,
  },
  /**
   * @description max length of a single tag
   */
  tagMaxLength: {
    type: Number,
    default: undefined,
  },
  /**
   * @description tag type, string or resolver
   */
  tagType: {
    type: definePropType<InputTagType | InputTagTypeResolver>([
      String,
      Function,
    ]),
    default: undefined,
  },
  /**
   * @description whether to show the count length badge
   */
  showCount: Boolean,
  /**
   * @description whether to show all tags in a popover
   */
  showPopover: Boolean,
  /**
   * @description whether to show the popover footer (clear all)
   */
  showMultipleFooter: Boolean,
  /**
   * @description text of the clear-all operation
   */
  multipleOperText: {
    type: String,
    default: '清空',
  },
  /**
   * @description show clear-all icon
   */
  allowClear: {
    type: Boolean,
    default: false,
  },
  /**
   * @description whether the before-addon has background
   */
  addonBeforeBg: Boolean,
  /**
   * @description whether the after-addon has background
   */
  addonAfterBg: Boolean,
  /**
   * @description prefix icon/content
   */
  prefixIcon: {
    type: iconPropType,
  },
  /**
   * @description suffix icon/content
   */
  suffixIcon: {
    type: iconPropType,
  },
} as const)
export type InputTagProps = ExtractPropTypes<typeof inputTagProps>

export const inputTagEmits = {
  [UPDATE_MODEL_EVENT]: (value: string[]) => Array.isArray(value),
  'multiple-change': (value: string[]) => Array.isArray(value),
  'input-change': (value: string) => typeof value === 'string',
  'tag-add': (tag: string, tags: string[]) =>
    typeof tag === 'string' && Array.isArray(tags),
  'tag-remove': (tag: string, tags: string[]) =>
    typeof tag === 'string' && Array.isArray(tags),
  change: (value: string[]) => Array.isArray(value),
  focus: (evt: FocusEvent) => evt instanceof FocusEvent,
  blur: (evt: FocusEvent) => evt instanceof FocusEvent,
  'press-enter': (evt: KeyboardEvent) => evt instanceof KeyboardEvent,
}
export type InputTagEmits = typeof inputTagEmits

export type InputTagInstance = InstanceType<typeof InputTag>
